import { Component, OnInit, OnDestroy, ViewChild } from "@angular/core";
import { Subscription, interval } from 'rxjs';
import { startWith, switchMap } from 'rxjs/operators';
import { DirectionService } from '../../direction.service';

import { AuthService } from "src/app/auth/auth.service";
import { UsersService } from 'src/app/super-management/user/users.service';

import { Router, ActivatedRoute } from "@angular/router";
import { DialogService } from 'src/app/dialog/dialog.service';

import { BranchesService } from 'src/app/super-management/branch/branches.service';
import { DataSharingService } from 'src/app/main-section/data-shering-service/data-sharing.service';

import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { copyTextToClipboard } from 'src/app/utils/clipboard-copy';

@Component({
  selector: "app-printing-que",
  templateUrl: "./printing-que.component.html",
  styleUrls: ["./printing-que.component.css"],
  host: {
    class: 'fill-screen'
  }
})

export class PrintingQueComponent implements OnInit, OnDestroy {
  isRTL: boolean = true;
  private directionSubscription: Subscription;
  isDarkMode: boolean = false;
  isLoading = false;

  userId: string;
  userIsAuthenticated = false;
  private authStatusSub: Subscription;

  branchUnique: string = '';
  branch: any;
  branchPapers: any[] = [];
  private branchUpdateSubscription: Subscription;

  private rolesSubscription: Subscription;
  roles: string[] = [];
  isSU: boolean = false;

  openMenuOrderIdQ: string | null = null;
  openMenuOrderIdPQ: string | null = null;
  @ViewChild('cancelOrderManuQ') cancelOrderMenuQ: any;
  @ViewChild('cancelOrderManuPQ') cancelOrderMenuPQ: any;

  isManigerMode: boolean = false;
  isEditMode: boolean = false;
  selectedOrderForMove: any = null;
  selectedOrderIndex: number = -1;

  constructor(
    private authService: AuthService,
    private directionService: DirectionService,
    private usersService: UsersService,
    private router: Router,
    private dialogService: DialogService,
    private route: ActivatedRoute,
    private branchesService: BranchesService,
    private dataShearingService: DataSharingService,
    private _snackBar: MatSnackBar,
    private translate: TranslateService,
  ) { }

  async ngOnInit() {
    this.isLoading = true;

    this.directionSubscription = this.directionService.direction$.subscribe(direction => {
      this.isRTL = direction === 'rtl';
    });

    this.directionService.isDarkMode$.subscribe(isDarkMode => {
      this.isDarkMode = isDarkMode;
    });

    this.branchUnique = this.route.snapshot.paramMap.get('branch');

    this.userIsAuthenticated = this.authService.getIsAuth();
    this.authStatusSub = this.authService
      .getAuthStatusListener()
      .subscribe(isAuthenticated => {
        this.userIsAuthenticated = isAuthenticated;
        this.userId = this.authService.getUserId();
      });

    this.rolesSubscription = this.authService.roles$.subscribe(roles => {
      this.roles = roles;
      if (this.roles.includes('su')) {
        this.isSU = true;
      } else {
        this.isSU = false;
      }
    });

    this.branchUpdateSubscription = interval(7000)
      .pipe(
        startWith(0),
        switchMap(() => this.branchesService.getBranchByUnique(this.branchUnique).toPromise())
      )
      .subscribe(
        branch => {
          const prevOpenOrderIdQ = this.openMenuOrderIdQ;
          const prevOpenOrderIdPQ = this.openMenuOrderIdPQ;
          this.branch = branch;
          this.branchPapers = this.branch?.printers[0]?.inputBins || [];
          this.isLoading = false;
          setTimeout(() => {
            if (prevOpenOrderIdQ) {
              const btnQ: HTMLElement | null = document.querySelector(`[data-order-id-q="${prevOpenOrderIdQ}"]`);
              if (btnQ) {
                btnQ.click();
              }
            }
            if (prevOpenOrderIdPQ) {
              const btnPQ: HTMLElement | null = document.querySelector(`[data-order-id-pq="${prevOpenOrderIdPQ}"]`);
              if (btnPQ) {
                btnPQ.click();
              }
            }
          });
        },
        error => {
          console.error('Error fetching and transforming branches:', error);
          throw error;
        }
      );
  }

  ngOnDestroy() {
    this.authStatusSub.unsubscribe();
    if (this.directionSubscription) {
      this.directionSubscription.unsubscribe();
    }
    if (this.branchUpdateSubscription) {
      this.branchUpdateSubscription.unsubscribe();
    }
  }

  isManiger() {
    if (this.roles && this.roles.includes('su') ||
      (this.roles && this.roles.includes('bm') && this.branch?.bm.toString() === this.userId)) {
      return true;
    }
    return false;
  }

  isSt() {
    if (this.roles && this.roles.includes('st') &&
      this.branch?.st?.some(st => st === this.userId)) {
      return true;
    }
    return false;
  }

  /** True for BM, ST, SU - can see paper-replacement dummy rows in queue */
  canSeePaperReplacementDummy(): boolean {
    return this.isSU || this.isManiger() || this.isSt();
  }

  private isPrinterQueueStoppedManually(): boolean {
    return this.branch?.printers?.[0]?.queueStatus === 'manual';
  }

  private hasAnyNotEnoughPaperInQueues(): boolean {
    const pq = this.branch?.printers?.[0]?.printerQueue;
    const q = this.branch?.printers?.[0]?.queue;

    const inPrinterQueue = Array.isArray(pq) ? pq.some((o: any) => !!o?.isNotEnoughPaper) : false;
    const inQueue = Array.isArray(q) ? q.some((o: any) => !!o?.isNotEnoughPaper) : false;

    return inPrinterQueue || inQueue;
  }

  /**
   * Show "paper replacement" dummy row:
   * - for orders that explicitly have isNotEnoughPaper
   * - OR, when printer queue is stopped manually and none of the orders has isNotEnoughPaper,
   *   show it only on the first order in printerQueue.
   */
  shouldShowPaperReplacementDummy(order: any, index: number, isPrinterQueue: boolean): boolean {
    if (!this.canSeePaperReplacementDummy()) {
      return false;
    }
    if (!!order?.isNotEnoughPaper) {
      return true;
    }
    return (
      isPrinterQueue === false &&
      index === 0 &&
      this.isPrinterQueueStoppedManually() &&
      !this.hasAnyNotEnoughPaperInQueues()
    );
  }

  getOrderPhone(order: any): string | null {
    return order?.userID?.phone || order?.user?.phone || null;
  }

  copyPhone(order: any): void {
    const phone = this.getOrderPhone(order);
    if (!phone) {
      return;
    }
    const phoneWithZero = String(phone).startsWith('0')
      ? String(phone)
      : '0' + String(phone);
    void copyTextToClipboard(phoneWithZero)
      .then(() => {
        this.translate
          .get(['su-management.branches_copy-success-phone', 'my-profile.phone'])
          .subscribe((res: { [key: string]: string }) => {
            const msg =
              res['su-management.branches_copy-success-phone'] +
              ' - ' +
              res['my-profile.phone'] +
              ' ' +
              phoneWithZero;
            this._snackBar.open(msg, '', {
              duration: 5000,
              verticalPosition: 'bottom',
              panelClass: ['auto-width', 'center-text'].filter(Boolean),
            });
          });
      })
      .catch((err) => console.error('Could not copy phone: ', err));
  }

  copyOrderDetails(order: any): void {
    const userId = order.userID?._id || order.userID;
    const details = `*EAZIX ORDER IN QUEUE*: User: *${order.user}* ,Printer *Plotter*, *${order.branchID.is_express ? order.printerID.serial_name : order.branchID.serial_name}*, Status: *${order.status}*, Pages: *${order.totalOrderLength}*, Created: *${order.created}*, Costs: Card: *${order.totalCost}* NIS + Eazix Points: *${order.pointsCost}*, link: _https://print.eazix.io/userorders/${userId}/${order._id}_, Order ID: *${order._id}*`;
    void copyTextToClipboard(details)
      .then(() => {
        this._snackBar.open(this.translate.instant('su-management.branches_copy-success-order'), '', {
          duration: 1500,
          panelClass: ['auto-width', 'center-text'].filter(Boolean),
        });
      })
      .catch((err) => console.error('Could not copy text: ', err));
  }

  goToNewOrder() {
    this.authService.updateAuthData('plotter', this.branch.serial_name);
    this.dataShearingService.setPrintingService('plotter');
    this.dataShearingService.setBranch(this.branch.serial_name);
    this.router.navigate(["/print"]);
  }

  getUserNumOfOrders() {
    if (!this.branch) {
      return 0;
    }
    let totalOrders = 0;
    if (this.branch.printers[0]?.printerQueue && this.branch.printers[0].printerQueue.some(order => order.user._id === localStorage.getItem('userId'))) {
      totalOrders += 1;
    }
    if (this.branch.printers[0]?.queue && this.branch.printers[0].queue.some(order => order.user._id === localStorage.getItem('userId'))) {
      totalOrders += 1;
    }
    return totalOrders;
  }

  getOrderTimeEstimation(branch: any, order: any, orderIndex: number, isInPrinter: boolean) {
    let totalOrdersListDurationInMinutes = 0;
    if (branch.printers[0]?.printerQueue && branch.printers[0]?.printerQueue.length > 0 && order?.totalOrderDurationInMinutes) {
      branch.printers[0]?.printerQueue.forEach((order, index) => {
        if (!isInPrinter || index < orderIndex) {
          totalOrdersListDurationInMinutes += order.totalOrderDurationInMinutes;
        }
      });
    }
    if ((branch.printers[0]?.queue && branch.printers[0]?.queue.length > 0 && order?.totalOrderDurationInMinutes) && !isInPrinter) {
      branch.printers[0]?.queue.forEach((order, index) => {
        if (index < orderIndex) {
          totalOrdersListDurationInMinutes += order.totalOrderDurationInMinutes;
        }
      });
    }
    return Math.ceil(totalOrdersListDurationInMinutes);
  }

  getTotalTimeEstimation(branch: any) {
    let totalBranchDurationInMinutes = 0;
    if (branch.printers[0]?.printerQueue && branch.printers[0]?.printerQueue.length > 0) {
      branch.printers[0]?.printerQueue.forEach((order, index) => {
        totalBranchDurationInMinutes += order.totalOrderDurationInMinutes;
      });
    }
    if (branch.printers[0]?.queue && branch.printers[0]?.queue.length > 0) {
      branch.printers[0]?.queue.forEach(order => {
        totalBranchDurationInMinutes += order.totalOrderDurationInMinutes;
      });
    }
    return Math.ceil(totalBranchDurationInMinutes);
  }

  getTotalNumOfOrders(branch: any) {
    let totalOrders = 0;
    if (branch.printers[0]?.printerQueue && branch.printers[0]?.printerQueue.length > 0) {
      totalOrders = branch.printers[0]?.printerQueue.length;
    }
    if (branch.printers[0]?.queue && branch.printers[0]?.queue.length > 0) {
      totalOrders += branch.printers[0]?.queue.length;
    }
    return totalOrders;
  }

  getUserNextOrderTimeEstimation() {
    if (!this.branch) {
      return false;
    }
    let nextOrder;
    if (this.branch.printers[0]?.printerQueue) {
      let index;
      nextOrder = this.branch.printers[0].printerQueue.find((order, i) => {
        if (order.user._id === localStorage.getItem('userId')) {
          index = i;
          return true;
        }
        return false;
      });
      if (nextOrder) {
        return this.getOrderTimeEstimation(this.branch, nextOrder, index, true);
      }
    }
    if (this.branch.printers[0]?.queue) {
      let index;
      nextOrder = this.branch.printers[0].queue.find((order, i) => {
        if (order.user._id === localStorage.getItem('userId')) {
          index = i;
          return true;
        }
        return false;
      });
      if (nextOrder) {
        return this.getOrderTimeEstimation(this.branch, nextOrder, index, false);
      }
    }
    return false;
  }

  orderIsInQueue(order: any): boolean {
    if (
      this.branch.printers[0]?.queue && this.branch.printers[0]?.queue.some(orderInQueue => orderInQueue._id === order._id)) {
      return true;
    }
    return false;
  }

  // MANIGMENT ===================

  cancelOrder(order: any, action: string) {
    this.branchesService.onCancelOrderFromQueue(action, order._id, this.branch.serial_name).subscribe(
      (res) => {
        this.dataShearingService.notifyPendingOrderDecrement();
        let msg;
        if (res.status === action) {
          msg = 'su-management.cancel-order.' + res.status + '.success';
        } else if (action === "REFUNDED" && res.status === 'CANCELLED') {
          msg = 'su-management.cancel-order.error.cancelled-but-not-refunded';
        }
        this._snackBar.open(this.translate.instant(msg), '', {
          duration: 5000,
          verticalPosition: 'top'
        });
      },
      (error) => {
        this._snackBar.open(this.translate.instant('su-management.cancel-order.error') + ' ' + error.message, '', {
          duration: 5000,
          verticalPosition: 'top'
        });
        throw error;
      }
    );
  }

  showUserManigment(order: any) {
    if (!this.isSU) {
      return;
    }
    const id = order?.userID?._id ?? order?.userID;
    if (id && typeof id !== 'object') {
      localStorage.setItem('searchedService', "plotter");
      localStorage.setItem('searchedBranchId', this.branch._id);
      this.router.navigate([`/userorders/${id}`]);
    }
  }

  PromoteOrderInQueue(order: any) {
    this.branchesService.onPromoteOrderInQueue(order._id, this.branch.serial_name).subscribe(
      (res) => {
        this._snackBar.open(this.translate.instant("promote-order.success"), '', {
          duration: 5000,
          verticalPosition: 'top'
        });
      },
      (error) => {
        this._snackBar.open(this.translate.instant('promote-order.error') + ' ' + error.message, '', {
          duration: 5000,
          verticalPosition: 'top'
        });
        throw error;
      }
    );
  }

  MoveOrderToPosition(order: any, targetIndex: number) {
    this.branchesService.onMoveOrderToPosition(order._id, this.branch.serial_name, targetIndex).subscribe(
      (res) => {
        this._snackBar.open(this.translate.instant("move-order.success"), '', {
          duration: 5000,
          verticalPosition: 'top'
        });
      },
      (error) => {
        let errorMessage = 'move-order.error';
        
        // Check for specific error messages from backend
        if (error.error && error.error.message === 'INVALID_POSITION') {
          errorMessage = 'error-content-INVALID_POSITION';
        }
        
        this._snackBar.open(this.translate.instant(errorMessage), '', {
          duration: 5000,
          verticalPosition: 'top'
        });
        throw error;
      }
    );
  }

  trackByPaperSerialName(index: number, paper: any): string {
    return (paper?.paperSerialName || '') + '_' + index;
  }

  getOrderPapers(order: any): any[] {
    let papersInOrder: any[] = [];
    if (!order || !order.files || order.files.length === 0) {
      return papersInOrder;
    }
    for (const file of order.files) {
      if (file.images && file.images.length > 0) {
        for (const image of file.images) {
          if (image.printSettings?.paperType) {
            const imagePaperType = image.printSettings.paperType;
            const paper = this.branchPapers.find(p => p.paperType.paperPrinterCode === imagePaperType) || null;
            if (paper) {
              const paperWidth = paper.paperType.paperWidth || 0;
              const imageWidth = image.imageWidth || 0;
              const imageHeight = image.imageHeight || 0;
              let imageLength = 0;
              if (imageWidth <= paperWidth && imageWidth <= paperWidth) {
                if (imageHeight > paperWidth) {
                  imageLength = paperWidth;
                } else {
                  imageLength = imageHeight;
                }
              } else {
                if (imageHeight > paperWidth) {
                  imageLength = imageHeight;
                } else {
                  imageLength = paperWidth;
                }
              }
              if (!papersInOrder.some(p => p.paperType === imagePaperType)) {
                papersInOrder.push({
                  paperType: image.printSettings.paperType,
                  paperSerialName: paper.paperType.paperType,
                  length: imageLength / 300 * 2.54,
                });
              } else {
                const existingPaper = papersInOrder.find(p => p.paperType === imagePaperType);
                if (existingPaper) {
                  existingPaper.length += imageLength / 300 * 2.54;
                }
              }
            }
          }
        }
      }
    }
    return papersInOrder;
  }

  roundDownTowDecimal(num: number): number {
    return Math.round(num) / 100;
  }

  toggleManigerView() {
    this.isManigerMode = !this.isManigerMode;
  }

  editPrintingQueue() {
    this.isEditMode = !this.isEditMode;
    if (!this.isEditMode) {
      // Clear selection when exiting edit mode
      this.selectedOrderForMove = null;
      this.selectedOrderIndex = -1;
    }
  }

  selectOrderForMove(order: any, index: number) {
    // If clicking the same order, deselect it
    if (this.selectedOrderForMove?._id === order._id) {
      this.selectedOrderForMove = null;
      this.selectedOrderIndex = -1;
      console.log('Order deselected');
    } else {
      this.selectedOrderForMove = order;
      this.selectedOrderIndex = index;
      console.log('Selected order for move:', order, 'at index:', index);
    }
  }

  moveOrderToPosition(targetIndex: number) {
    if (!this.selectedOrderForMove) {
      return;
    }
    
    // Check if target position is valid
    const queueLength = this.branch?.printers[0]?.queue?.length || 0;
    if (targetIndex < 0 || targetIndex > queueLength - 1) {
      console.error('Invalid target position:', targetIndex, 'Queue length:', queueLength);
      this._snackBar.open('מיקום לא תקין - המיקום מחוץ לגבולות התור', '', {
        duration: 3000,
        verticalPosition: 'top'
      });
      return;
    }
    
    // Calculate the final position after the selected order is removed
    // The backend does: splice(currentIndex, 1) then splice(newPosition, 0, order)
    // So if we're moving down, the indices shift left by 1 after removal
    let finalPosition = targetIndex;
    if (this.selectedOrderIndex < targetIndex) {
      // Moving down: target position shifts left after removal
      // But since we want to be AFTER the target, no adjustment needed
      finalPosition = targetIndex;
    } else if (this.selectedOrderIndex > targetIndex) {
      // Moving up: we want to be AFTER the target, so add 1
      // But can't exceed queue length after removal (queueLength - 1)
      finalPosition = Math.min(targetIndex + 1, queueLength - 1);
    }
    
    console.log('Move order', this.selectedOrderForMove._id, 'to position', finalPosition);
    console.log('Queue length:', queueLength);
    console.log('Selected order current index:', this.selectedOrderIndex);
    console.log('Target index clicked:', targetIndex);
    console.log('Final position:', finalPosition);
    
    // Call the API to move the order
    this.MoveOrderToPosition(this.selectedOrderForMove, finalPosition);
    
    // Clear selection after initiating the move
    this.selectedOrderForMove = null;
    this.selectedOrderIndex = -1;
  }

  // ========================
}
